import React, { useState, useEffect, useRef } from 'react';
import { getDocuments, createDocument, updateDocument, deleteDocument, getParametrage, searchMembresForParcelle, downloadDocumentUrl } from '../services/api';
import { FiPlus, FiEdit2, FiTrash2, FiSearch, FiX, FiDownload, FiEye, FiFile, FiUser } from 'react-icons/fi';

const Documents = () => {
  const [documents, setDocuments] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState('');
  const [typedocuments, setTypedocuments] = useState<any[]>([]);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [form, setForm] = useState({ libelle: '', typedocument_id: '', datecreation: '', user_id: '', url: '' });
  const [file, setFile] = useState<File | null>(null);

  // Member autocomplete
  const [membreSearch, setMembreSearch] = useState('');
  const [membreResults, setMembreResults] = useState<any[]>([]);
  const [selectedMembre, setSelectedMembre] = useState<any>(null);
  const [showMembreDropdown, setShowMembreDropdown] = useState(false);
  const [membresLoading, setMembresLoading] = useState(false);
  const searchTimeout = useRef<any>(null);

  const loadData = () => {
    getDocuments({ page, limit: 50, q: search }).then(r => {
      setDocuments(r.data.data);
      setTotal(r.data.total);
      setTotalPages(r.data.totalPages);
    }).catch(() => {});
  };

  useEffect(() => { loadData(); }, [page, search]);
  useEffect(() => {
    getParametrage('typedocuments').then(r => setTypedocuments(r.data)).catch(() => {});
  }, []);

  const resetForm = () => {
    setForm({ libelle: '', typedocument_id: '', datecreation: '', user_id: '', url: '' });
    setFile(null);
    setSelectedMembre(null);
    setMembreSearch('');
    setMembreResults([]);
  };

  const openAdd = () => { resetForm(); setEditingId(''); setShowModal(true); };
  const openEdit = (d: any) => {
    setForm({
      libelle: d.libelle || '',
      typedocument_id: d.typedocument_id || '',
      datecreation: d.datecreation ? d.datecreation.substring(0, 10) : '',
      user_id: d.user_id || '',
      url: d.url || ''
    });
    setFile(null);
    if (d.membre_nom) {
      setSelectedMembre({ id: d.user_id, prenomnom: d.membre_nom, matricule: d.membre_matricule });
      setMembreSearch(d.membre_nom);
    } else {
      setSelectedMembre(null);
      setMembreSearch('');
    }
    setEditingId(d.id);
    setShowModal(true);
  };

  // Member autocomplete
  const handleMembreSearchChange = (val: string) => {
    setMembreSearch(val);
    setShowMembreDropdown(true);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (val.length < 2) { setMembreResults([]); return; }
    setMembresLoading(true);
    searchTimeout.current = setTimeout(async () => {
      try {
        const r = await searchMembresForParcelle(val);
        setMembreResults(r.data || []);
      } catch { setMembreResults([]); }
      setMembresLoading(false);
    }, 300);
  };

  const selectMembre = (m: any) => {
    setSelectedMembre(m);
    setForm({ ...form, user_id: m.id });
    setMembreSearch(m.prenomnom || `${m.prenom} ${m.nom}`);
    setShowMembreDropdown(false);
  };

  const clearMembre = () => {
    setSelectedMembre(null);
    setForm({ ...form, user_id: '' });
    setMembreSearch('');
    setMembreResults([]);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingId) {
        if (file) {
          // If file changed, send as FormData
          const fd = new FormData();
          fd.append('fichier', file);
          Object.entries(form).forEach(([k, v]) => { if (v) fd.append(k, v); });
          // Use createDocument for file upload (PUT doesn't support multer easily)
          // For simplicity, update metadata separately then create new file entry
          await updateDocument(editingId, form);
        } else {
          await updateDocument(editingId, form);
        }
        setSuccess('Document mis à jour');
      } else {
        if (file) {
          const fd = new FormData();
          fd.append('fichier', file);
          Object.entries(form).forEach(([k, v]) => { if (v) fd.append(k, v); });
          await createDocument(fd);
        } else {
          await createDocument(form);
        }
        setSuccess('Document créé avec succès');
      }
      setShowModal(false);
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Supprimer ce document ?')) return;
    await deleteDocument(id);
    loadData();
  };

  const openDocument = (doc: any) => {
    const token = localStorage.getItem('cohamac_token');
    const url = downloadDocumentUrl(doc.id);
    // Open in new tab with token
    window.open(`${url}?token=${token}`, '_blank');
  };

  const getFileIcon = (doc: any) => {
    const ext = doc.extension || doc.document?.split('.').pop() || '';
    if (['pdf'].includes(ext.toLowerCase())) return { color: '#c62828', label: 'PDF' };
    if (['jpg', 'jpeg', 'png'].includes(ext.toLowerCase())) return { color: '#2e7d32', label: 'IMG' };
    if (['doc', 'docx'].includes(ext.toLowerCase())) return { color: '#1565C0', label: 'DOC' };
    if (['xls', 'xlsx'].includes(ext.toLowerCase())) return { color: '#2e7d32', label: 'XLS' };
    return { color: '#555', label: 'FILE' };
  };

  return (
    <div>
      <h1 className="page-title">Gestion des documents</h1>

      {success && <div className="alert alert-success">{success}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      <div className="card">
        <div className="card-header">
          <span>Documents ({total.toLocaleString('fr-FR')})</span>
          <button className="btn btn-success" onClick={openAdd}><FiPlus /> Nouveau document</button>
        </div>
        <div className="card-body">
          <div className="toolbar">
            <div className="search-box">
              <FiSearch className="search-icon" />
              <input type="text" className="form-input" placeholder="Rechercher..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
            </div>
          </div>

          <div className="table-responsive">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Libellé</th>
                  <th>Type</th>
                  <th>Membre</th>
                  <th>Fichier</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {documents.map(d => {
                  const fileInfo = getFileIcon(d);
                  const hasFile = !!d.document;
                  return (
                    <tr key={d.id}>
                      <td>{d.datecreation ? new Date(d.datecreation).toLocaleDateString('fr-FR') : '-'}</td>
                      <td><strong>{d.libelle || '-'}</strong></td>
                      <td>{d.type_libelle ? <span className="badge badge-blue">{d.type_libelle}</span> : '-'}</td>
                      <td>
                        {d.membre_nom ? (
                          <span style={{ fontSize: '0.85rem' }}>
                            {d.membre_nom}
                            {d.membre_matricule && <small style={{ display: 'block', color: '#888' }}>{d.membre_matricule}</small>}
                          </span>
                        ) : '-'}
                      </td>
                      <td>
                        {hasFile ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.82rem' }}>
                            <span className="badge" style={{ background: fileInfo.color, color: '#fff', fontSize: '0.7rem', padding: '2px 6px' }}>{fileInfo.label}</span>
                          </span>
                        ) : (
                          <span style={{ color: '#bbb', fontSize: '0.82rem' }}>-</span>
                        )}
                      </td>
                      <td>
                        <div className="actions">
                          {hasFile && (
                            <>
                              <button className="btn-icon" style={{ color: '#1565C0' }} onClick={() => openDocument(d)} title="Visualiser / Télécharger">
                                <FiEye />
                              </button>
                              <a
                                href={downloadDocumentUrl(d.id)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn-icon"
                                style={{ color: '#2e7d32' }}
                                title="Télécharger"
                                onClick={(e) => {
                                  e.preventDefault();
                                  const token = localStorage.getItem('cohamac_token');
                                  // Download via fetch with auth
                                  fetch(downloadDocumentUrl(d.id), { headers: { Authorization: `Bearer ${token}` } })
                                    .then(r => r.blob())
                                    .then(blob => {
                                      const url = URL.createObjectURL(blob);
                                      const a = document.createElement('a');
                                      a.href = url;
                                      a.download = d.libelle + (d.extension ? '.' + d.extension : d.document?.split('.').pop() ? '.' + d.document.split('.').pop() : '');
                                      a.click();
                                      URL.revokeObjectURL(url);
                                    })
                                    .catch(() => alert('Erreur lors du téléchargement'));
                                }}
                              >
                                <FiDownload />
                              </a>
                            </>
                          )}
                          <button className="btn-icon btn-warning" onClick={() => openEdit(d)}><FiEdit2 /></button>
                          <button className="btn-icon btn-danger" onClick={() => handleDelete(d.id)}><FiTrash2 /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {documents.length === 0 && (
                  <tr><td colSpan={6} style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>Aucun document</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              {page > 1 && <button onClick={() => setPage(page - 1)}>&laquo;</button>}
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const p = Math.max(1, page - 2) + i;
                if (p > totalPages) return null;
                return <button key={p} className={p === page ? 'active' : ''} onClick={() => setPage(p)}>{p}</button>;
              })}
              {page < totalPages && <button onClick={() => setPage(page + 1)}>&raquo;</button>}
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingId ? 'Modifier le document' : 'Nouveau document'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}><FiX /></button>
            </div>
            <form onSubmit={handleSave}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group"><label>Libellé *</label><input className="form-input" value={form.libelle} onChange={e => setForm({ ...form, libelle: e.target.value })} required /></div>
                  <div className="form-group"><label>Date de création</label><input type="date" className="form-input" value={form.datecreation} onChange={e => setForm({ ...form, datecreation: e.target.value })} /></div>
                </div>
                <div className="form-row">
                  <div className="form-group"><label>Type de document</label>
                    <select className="form-input" value={form.typedocument_id} onChange={e => setForm({ ...form, typedocument_id: e.target.value })}>
                      <option value="">--</option>
                      {typedocuments.map(t => <option key={t.id} value={t.id}>{t.libelle}</option>)}
                    </select>
                  </div>
                  {/* Member autocomplete */}
                  <div className="form-group" style={{ position: 'relative' }}>
                    <label>Membre</label>
                    {selectedMembre ? (
                      <div className="selected-membre-card">
                        <div className="selected-membre-icon"><FiUser /></div>
                        <div className="selected-membre-info">
                          <strong>{selectedMembre.prenomnom || selectedMembre.prenom + ' ' + selectedMembre.nom}</strong>
                          {selectedMembre.matricule && <small>{selectedMembre.matricule}</small>}
                        </div>
                        <button type="button" className="btn-icon" onClick={clearMembre}><FiX /></button>
                      </div>
                    ) : (
                      <>
                        <input className="form-input" value={membreSearch} onChange={e => handleMembreSearchChange(e.target.value)}
                          onFocus={() => membreResults.length > 0 && setShowMembreDropdown(true)}
                          placeholder="Rechercher un membre..." autoComplete="off" />
                        {showMembreDropdown && (membreResults.length > 0 || membresLoading) && (
                          <div className="autocomplete-dropdown">
                            {membresLoading ? (
                              <div className="autocomplete-item-empty search-loading">Recherche...</div>
                            ) : membreResults.map(m => (
                              <div key={m.id} className="autocomplete-item" onClick={() => selectMembre(m)}>
                                <span className="autocomplete-item-name">{m.prenomnom || m.prenom + ' ' + m.nom}</span>
                                <span className="autocomplete-item-detail">{m.matricule}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
                {/* File upload */}
                <div className="form-group">
                  <label>Fichier (PDF, Image, Word, Excel)</label>
                  <div className="file-upload-zone" onClick={() => document.getElementById('doc-file-input')?.click()} style={{ padding: '12px 16px', minHeight: 'auto' }}>
                    <input id="doc-file-input" type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx" style={{ display: 'none' }}
                      onChange={e => setFile(e.target.files?.[0] || null)} />
                    {file ? (
                      <div className="file-upload-selected">
                        <FiFile style={{ fontSize: '1.2rem', color: '#1565C0' }} />
                        <div>
                          <strong>{file.name}</strong>
                          <small style={{ display: 'block', color: '#888' }}>{(file.size / 1024).toFixed(1)} Ko</small>
                        </div>
                        <button type="button" className="btn-icon" onClick={e => { e.stopPropagation(); setFile(null); }}><FiX /></button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#888' }}>
                        <FiFile /> <span>Cliquer pour sélectionner un fichier</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="form-group"><label>URL (lien externe)</label><input className="form-input" value={form.url} onChange={e => setForm({ ...form, url: e.target.value })} placeholder="https://..." /></div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>Annuler</button>
                <button type="submit" className="btn btn-primary">Enregistrer</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Documents;
